/**
 * Build Status Banner — Surfaces a failed GitHub Actions build at the top of
 * the editor with one-click Repair package.json and Rebuild actions, so the
 * fix is discoverable without opening the Versions panel.
 *
 * Polls /builds/:projectId every 20s and only renders when the most recent
 * build status is 'failed'. Auto-refreshes the error from GitHub Actions
 * logs when the stored error is the generic "Build failed — see <url>"
 * placeholder, so users see the real npm/build error inline.
 */

import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Wrench,
  RefreshCw,
  ExternalLink,
  Loader2,
  X,
  ChevronDown,
  ChevronUp,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { useRepairPackageJson } from '../../hooks/useRepairPackageJson';
import { useAuthStore } from '../../stores/auth';
import { useHealBuild } from '../../hooks/useHealBuild';

interface BuildStatusBannerProps {
  projectId: string;
}

interface Build {
  _id: string;
  status: 'queued' | 'building' | 'deploying' | 'success' | 'failed';
  commitSha: string;
  branch: string;
  error?: string;
  workersBuildId?: string;
  metadata?: { githubRunId?: string; githubRunUrl?: string };
  createdAt: string;
}

// Pattern that recognises npm/package.json failures we know how to auto-repair.
const PKG_JSON_ERROR_RE =
  /EINVALIDPACKAGENAME|EINVALIDTAGNAME|Invalid package name|invalid version|npm install|npm ERR/i;

// Runtime / prerender errors — these are bugs in the AI-generated source code
// and are best fixed by the AI heal pass (Repair package.json won't help).
const RUNTIME_ERROR_RE =
  /TypeError|Cannot read propert|prerender(ing)? page|Export encountered an error|ReferenceError|undefined is not|Module not found/i;

// The webhook posts this generic message when GitHub Actions just hands us
// the run URL with no real error body. We try to enrich it from logs.
const GENERIC_BUILD_ERROR_RE = /^(Workers Build failed: )?Build failed — see https?:\/\//i;

export function BuildStatusBanner({ projectId }: BuildStatusBannerProps) {
  const [dismissedBuildId, setDismissedBuildId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const refreshedFor = useRef<string | null>(null);
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['builds', projectId, 'latest'],
    queryFn: async () => {
      const res = await api.get(`/builds/${projectId}`);
      return (res.data?.data?.builds as Build[]) || [];
    },
    refetchInterval: 20000,
  });

  // Use the most recent *terminal* build (failed/success). A pending
  // queued/building build can sit at index [0] and would otherwise mask a
  // prior failure that the user still needs to repair.
  const latestTerminal = data?.find((b) => b.status === 'failed' || b.status === 'success');
  const inFlight = data?.find(
    (b) => b.status === 'queued' || b.status === 'building' || b.status === 'deploying'
  );
  const latest = latestTerminal;
  const repairPkgJson = useRepairPackageJson(projectId);
  const healBuild = useHealBuild(projectId);

  const triggerBuild = useMutation({
    mutationFn: async () => {
      const res = await api.post(`/builds/${projectId}/trigger`);
      return res.data.data;
    },
    onSuccess: () => {
      toast.success('Rebuild triggered');
      queryClient.invalidateQueries({ queryKey: ['builds', projectId, 'latest'] });
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message || 'Failed to trigger build'),
  });

  const refreshError = useMutation({
    mutationFn: async ({ silent: _silent }: { silent: boolean } = { silent: false }) => {
      const res = await api.post(`/builds/${projectId}/refresh-error`);
      return res.data.data as { error: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['builds', projectId, 'latest'] });
    },
    onError: (err: any, variables) => {
      // Auto-fetches on banner mount shouldn't blast a red toast — they happen
      // on every page load while a failed build is around. Only toast on
      // user-clicked refreshes.
      if (variables?.silent) return;
      const msg = err?.response?.data?.error?.message;
      if (msg && msg !== 'Build is not linked to a GitHub Actions run') {
        toast.error(msg);
      }
    },
  });

  // Auto-fetch the real error from GitHub Actions logs once per failed build
  // when the stored error is just the generic "Build failed — see <url>" line.
  useEffect(() => {
    if (!latest || latest.status !== 'failed') return;
    if (refreshedFor.current === latest._id) return;
    if (latest.error && !GENERIC_BUILD_ERROR_RE.test(latest.error.trim())) return;
    if (!latest.metadata?.githubRunId) return;
    refreshedFor.current = latest._id;
    refreshError.mutate({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latest?._id, latest?.status, latest?.error]);

  if (!latest || latest.status !== 'failed') return null;
  if (dismissedBuildId === latest._id) return null;

  const errorText = latest.error || '';
  const looksLikePkgJsonIssue = PKG_JSON_ERROR_RE.test(errorText);
  const looksLikeRuntimeIssue = RUNTIME_ERROR_RE.test(errorText) && !looksLikePkgJsonIssue;
  const isGenericError = GENERIC_BUILD_ERROR_RE.test(errorText.trim());
  const isMultiLine = errorText.includes('\n');
  // Repo / CI links are platform plumbing — customers never see GitHub.
  const isSuperAdmin = useAuthStore.getState().user?.role === 'SUPER_ADMIN';
  const runUrl = isSuperAdmin
    ? latest.metadata?.githubRunUrl ||
      (latest.workersBuildId
        ? `https://dash.cloudflare.com/?to=/:account/workers-and-pages/builds/${latest.workersBuildId}`
        : null)
    : null;

  return (
    <div className="border-b border-amber-200 bg-amber-50 px-4 py-2.5">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-sm font-medium text-amber-900">
            Latest build failed
            <span className="font-mono text-[10px] text-amber-700">
              {latest.commitSha?.slice(0, 7)}
            </span>
            {inFlight && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-normal text-amber-700">
                <Loader2 className="h-3 w-3 animate-spin" />
                rebuild {inFlight.status}
              </span>
            )}
            {refreshError.isPending && (
              <span className="inline-flex items-center gap-1 text-[10px] font-normal text-amber-700">
                <Loader2 className="h-3 w-3 animate-spin" />
                fetching log…
              </span>
            )}
          </div>
          {errorText && (
            <>
              {isMultiLine && expanded ? (
                <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap rounded border border-amber-200 bg-white/70 p-2 font-mono text-[11px] leading-snug text-amber-900">
                  {errorText}
                </pre>
              ) : (
                <p className="mt-0.5 line-clamp-2 whitespace-pre-wrap text-xs text-amber-800">
                  {errorText.slice(0, 240)}
                  {errorText.length > 240 ? '…' : ''}
                </p>
              )}
              {isMultiLine && (
                <button
                  onClick={() => setExpanded((v) => !v)}
                  className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 hover:text-amber-900"
                >
                  {expanded ? (
                    <>
                      <ChevronUp className="h-3 w-3" /> Hide log
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-3 w-3" /> Show full log
                    </>
                  )}
                </button>
              )}
            </>
          )}
          {looksLikePkgJsonIssue && (
            <p className="mt-1 text-[11px] text-amber-700">
              Detected a bad <code className="rounded bg-amber-100 px-1">package.json</code>. The
              platform will sanitize and rebuild automatically.
            </p>
          )}
          {looksLikeRuntimeIssue && (
            <p className="mt-1 text-[11px] text-amber-700">
              Detected a runtime / prerender error. The platform is healing this automatically — a
              fix commit will land in a moment.
            </p>
          )}
          {isSuperAdmin && isGenericError && !refreshError.isPending && (
            <button
              onClick={() => refreshError.mutate({ silent: false })}
              className="mt-1 text-[11px] font-medium text-amber-700 underline hover:text-amber-900"
            >
              Fetch real error from GitHub Actions
            </button>
          )}
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          {runUrl && (
            <a
              href={runUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs text-amber-800 hover:bg-amber-100"
              title="Open build logs"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Logs
            </a>
          )}
          <button
            onClick={() => setShowAdvanced((v) => !v)}
            className="rounded-md px-2 py-1.5 text-[11px] text-amber-700 hover:bg-amber-100"
            title="Manual overrides (rarely needed)"
          >
            {showAdvanced ? 'Hide advanced' : 'Advanced'}
          </button>
          <button
            onClick={() => setDismissedBuildId(latest._id)}
            className="rounded p-1 text-amber-600 hover:bg-amber-100"
            title="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {showAdvanced && (
        <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-amber-200 pl-7 pt-2">
          <span className="text-[11px] text-amber-700">
            Manual overrides — auto-heal is already running in the background:
          </span>
          <button
            onClick={() => healBuild.mutate()}
            disabled={healBuild.isPending}
            className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
            title="Force another AI heal pass (skips the chain-suspension cap)"
          >
            {healBuild.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="h-3.5 w-3.5" />
            )}
            Heal with AI
          </button>
          <button
            onClick={() => repairPkgJson.mutate()}
            disabled={repairPkgJson.isPending}
            className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
            title="Sanitize package.json on GitHub and trigger a fresh build"
          >
            {repairPkgJson.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Wrench className="h-3.5 w-3.5" />
            )}
            Repair package.json
          </button>
          <button
            onClick={() => triggerBuild.mutate()}
            disabled={triggerBuild.isPending}
            className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
            title="Trigger a fresh build of the current main branch"
          >
            {triggerBuild.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            Rebuild
          </button>
        </div>
      )}
    </div>
  );
}
